import { type ElementType, FC, HTMLAttributes, ReactNode } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { Slot } from '@radix-ui/react-slot';
import { Icon, type IconName, Pictogram, type PictogramName, Skeleton, SkeletonTextLines } from '../../../core-components';
import { MeinSchiffText } from '../MeinSchiffText';
type Fact = {
  icon?: IconName;
  text?: string;
};
type DetailedFallback = {
  headline?: boolean;
  subline?: boolean;
  pictogram?: boolean;
};
export type HeaderSectionProps = HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  fallback?: boolean | DetailedFallback;
  pictogram?: PictogramName;
  headline?: ReactNode;
  headlineAs?: ElementType;
  subline?: ReactNode;
  disableSublineWidthRestriction?: boolean;
  centered?: boolean;
  facts?: Fact[] | string[];
};
export const HeaderSection: FC<HeaderSectionProps> = ({
  asChild,
  fallback: fallbackArg,
  pictogram,
  headline,
  headlineAs: HeadlineTag = 'div',
  subline,
  disableSublineWidthRestriction,
  centered,
  facts: factsArg = [],
  ...attrs
}) => {
  const facts = factsArg.map((fact): Fact | undefined => {
    if (typeof fact === 'string') {
      return {
        icon: 'check',
        text: fact
      };
    }
    if (typeof fact.text === 'undefined') {
      return undefined;
    }
    return fact;
  }).filter((fact): fact is NonNullable<typeof fact> => {
    return typeof fact !== 'undefined';
  });
  const Element = asChild ? Slot : 'div';
  const fallback = getDetailedFallback(fallbackArg);
  const anyFallback = fallback.headline || fallback.subline || fallback.pictogram;
  return <Element {...attrs} className={twMerge('space-y-4 text-marine-high-emphasis', attrs.className)} data-sentry-element="Element" data-sentry-component="HeaderSection" data-sentry-source-file="HeaderSection.tsx">
      <div className={twJoin(!centered && 'flex items-center gap-2 sm:gap-4', centered && 'space-y-4 text-center')}>
        {fallback?.pictogram && <Skeleton className={twJoin('aspect-1/1', !centered && 'w-[40px] sm:w-[60px]', centered && 'mx-auto w-[60px]')} />}
        {!fallback.pictogram && pictogram && <Pictogram name={pictogram} size="md" className={twJoin(!centered && 'block [&_svg]:size-[40px] [&_svg]:sm:size-[60px]')} />}
        {fallback.headline && <Skeleton className="inline-block h-6 w-1/2" />}
        {!fallback.headline && headline && <HeadlineTag className="headline-lg print:headline-md">
            <MeinSchiffText>{headline}</MeinSchiffText>
          </HeadlineTag>}
      </div>
      {fallback.subline && <>
          <SkeletonTextLines number={3} className="inline-block" wrapperClassName="text-center sm:hidden" />
          <SkeletonTextLines number={1} wrapperClassName="hidden sm:block" />
        </>}
      {!fallback.subline && subline && <div className={twJoin('text-lg print:text-base', !disableSublineWidthRestriction && 'max-w-[856px]', centered && 'mx-auto text-center')}>
          {subline}
        </div>}

      {!anyFallback && facts.length > 0 && <ul className="space-y-3">
          {facts.map((fact, idx) => <li key={idx} className="flex items-start gap-2">
              {fact.icon && <div className="py-0.5">
                  <Icon name={fact.icon} size="sm" />
                </div>}
              {fact.text && <div className="text-lg">{fact.text}</div>}
            </li>)}
        </ul>}
    </Element>;
};
const getDetailedFallback = (fallback: boolean | DetailedFallback | undefined): DetailedFallback => {
  if (typeof fallback === 'undefined' || typeof fallback === 'boolean') {
    return {
      headline: fallback === true,
      subline: fallback === true,
      pictogram: fallback === true
    };
  }
  return fallback;
};